import { useEffect, useState } from "react";

const COOKIE_NAME = "eigoo_consent";
const MAX_AGE_DAYS = 365;

function readCookie(name) {
    if (typeof document === "undefined") return null;
    const match = document.cookie
        .split("; ")
        .find((row) => row.startsWith(`${name}=`));
    return match ? match.split("=")[1] : null;
}

function writeCookie(name, value, days) {
    if (typeof document === "undefined") return;
    const maxAge = days * 24 * 60 * 60;
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${name}=${value}; Path=/; Max-Age=${maxAge}; SameSite=Lax${secure}`;
}

export default function CookieBanner() {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (readCookie(COOKIE_NAME) === "true") return;
        // Delay a beat so it fades in after the hero appears
        const t = setTimeout(() => setVisible(true), 800);
        return () => clearTimeout(t);
    }, []);

    const accept = () => {
        writeCookie(COOKIE_NAME, "true", MAX_AGE_DAYS);
        setVisible(false);
    };

    if (!visible) return null;

    return (
        <>
            <style>{`
                @keyframes eigooCookieFade {
                    from { opacity: 0; transform: translateY(24px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                .eigoo-cookie {
                    position: fixed;
                    left: 50%;
                    bottom: 24px;
                    transform: translateX(-50%);
                    z-index: 9999;
                    width: calc(100% - 32px);
                    max-width: 720px;
                    background: #1a1a1a;
                    color: #f3f3f3;
                    border: 1px solid rgba(212, 175, 55, 0.35);
                    box-shadow:
                        0 24px 60px -20px rgba(0, 0, 0, 0.9),
                        0 0 0 1px rgba(0, 0, 0, 0.4);
                    padding: 18px 20px;
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    font-family: "Outfit", -apple-system, BlinkMacSystemFont, sans-serif;
                    animation: eigooCookieFade 0.6s cubic-bezier(0.22, 1, 0.36, 1) both;
                }
                .eigoo-cookie__text {
                    flex: 1;
                    font-size: 13px;
                    line-height: 1.55;
                    color: #d5d5d5;
                }
                .eigoo-cookie__text strong {
                    color: #e6d59a;
                    font-weight: 600;
                    letter-spacing: 0.02em;
                }
                .eigoo-cookie__btn {
                    background: linear-gradient(180deg, #e6d59a 0%, #d4af37 60%, #aa8529 100%);
                    color: #0a0a0a;
                    border: 1px solid rgba(255, 230, 160, 0.35);
                    padding: 10px 22px;
                    font-family: inherit;
                    font-weight: 600;
                    font-size: 11px;
                    letter-spacing: 0.22em;
                    text-transform: uppercase;
                    cursor: pointer;
                    box-shadow:
                        inset 0 1px 0 rgba(255, 255, 255, 0.4),
                        0 8px 20px -8px rgba(212, 175, 55, 0.55);
                    transition:
                        transform 0.2s ease,
                        box-shadow 0.2s ease;
                    flex-shrink: 0;
                }
                .eigoo-cookie__btn:hover {
                    transform: translateY(-1px);
                    box-shadow:
                        inset 0 1px 0 rgba(255, 255, 255, 0.5),
                        0 12px 26px -6px rgba(212, 175, 55, 0.7);
                }
                .eigoo-cookie__btn:active {
                    transform: translateY(0);
                }
                @media (max-width: 640px) {
                    .eigoo-cookie {
                        flex-direction: column;
                        align-items: stretch;
                        text-align: left;
                        padding: 16px;
                        bottom: 16px;
                    }
                    .eigoo-cookie__btn {
                        width: 100%;
                        padding: 12px 20px;
                    }
                }
            `}</style>
            <div
                className="eigoo-cookie"
                role="dialog"
                aria-live="polite"
                aria-label="Cookie consent"
                data-testid="cookie-banner"
            >
                <p className="eigoo-cookie__text">
                    <strong>Cookie Notice.</strong> This site uses cookies to enhance your
                    experience. By continuing, you consent to our use of cookies.
                </p>
                <button
                    type="button"
                    className="eigoo-cookie__btn"
                    onClick={accept}
                    data-testid="cookie-accept-btn"
                >
                    Got it
                </button>
            </div>
        </>
    );
}
